<script setup lang="ts">
import { ref, computed } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useDialog } from '@/shared/composables/useDialog';
import ContactFileUploadSection from '../components/ContactFileUploadSection.vue';
import ContactFileUploadContentSection from '../components/ContactFileUploadContentSection.vue';
import { contactsApi } from '../api/contactsApi';
import type { FileUploadData, ColumnMapping, ValidatedRow, CreateIndividualData, CreateCorporateData } from '../types/contact.types';

interface Props {
    modelValue: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    'update:modelValue': [value: boolean];
    'upload-complete': [];
}>();

const { createSyncT } = useTranslation();
const currentLanguage = ref(localStorage.getItem('preferred-language') || 'en');
const t = createSyncT(currentLanguage);
const { alert } = useDialog();

const dialog = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value)
});

// 단계 관리
const currentStep = ref<1 | 2 | 3 | 4>(1);
const contactType = ref<'Individual' | 'Corporate' | ''>('Individual');
const uploadedFile = ref<File | null>(null);
const fileData = ref<FileUploadData | null>(null);
const mappings = ref<ColumnMapping>({});
const validatedData = ref<ValidatedRow[]>([]);
const isUploading = ref(false);
const contentSectionRef = ref<InstanceType<typeof ContactFileUploadContentSection> | null>(null);

// 파일 업로드 완료 처리
const handleFileUploaded = (data: FileUploadData) => {
    fileData.value = data;
    currentStep.value = 2;
};

// 매핑 업데이트
const handleMappingUpdated = (newMappings: ColumnMapping) => {
    mappings.value = newMappings;
};

// Validation 완료 처리
const handleValidationComplete = (validated: ValidatedRow[]) => {
    console.log('handleValidationComplete 호출:', {
        validatedCount: validated.length,
        validated: validated
    });
    validatedData.value = validated;
    console.log('validatedData 업데이트 후:', validatedData.value);
    
    // 2단계에 유지 (3단계로 이동하지 않음)
    // Validation 완료 후 canUpload가 true이면 Upload 버튼이 표시됨
};

// Next 버튼 활성화 조건
const canProceedToNext = computed(() => {
    if (currentStep.value === 1) {
        return contactType.value !== '' && uploadedFile.value !== null && fileData.value !== null;
    } else if (currentStep.value === 2) {
        // 필수 필드 매핑 완료 확인
        const systemColumns = contactType.value === 'Individual' 
            ? [
                { key: 'ID_TYPE_CD', required: true },
                { key: 'NATIONALITY_CD', required: true },
                { key: 'FIRST_NAME', required: true },
                { key: 'SURNAME', required: true },
                { key: 'CONTACT_EMAIL', required: true },
            ]
            : [
                { key: 'JURISDICTION', required: true },
                { key: 'AUDITOR_YN', required: true },
                { key: 'CONTACT_NAME', required: true },
                { key: 'CONTACT_REG_NO', required: true },
            ];
        
        return systemColumns.every(col => 
            mappings.value[col.key] !== null && 
            mappings.value[col.key] !== undefined
        );
    } else if (currentStep.value === 3) {
        // Validation 완료 확인
        return validatedData.value.length > 0;
    }
    return false;
});

// Upload 버튼 활성화 조건 (2단계에서만)
const canUpload = computed(() => {
    // 2단계에서만 Upload 버튼 활성화 가능
    if (currentStep.value !== 2) return false;
    
    // validatedData가 없으면 false
    if (!validatedData.value || validatedData.value.length === 0) return false;
    
    // 중복 데이터 체크
    const hasDuplicates = validatedData.value.some(row => row.isDuplicate);
    if (hasDuplicates) {
        console.log('canUpload: 중복 데이터 있음');
        return false;
    }
    
    // 필수값 체크
    const hasMissingRequired = validatedData.value.some(row => row.isMissingRequired);
    if (hasMissingRequired) {
        console.log('canUpload: 필수값 누락');
        return false;
    }
    
    console.log('canUpload: true', { 
        step: currentStep.value, 
        validatedCount: validatedData.value.length,
        validatedData: validatedData.value 
    });
    return true;
});

// Next 버튼 클릭
const handleNext = () => {
    if (currentStep.value === 1) {
        // 파일 업로드 완료 확인 후 2단계로 이동
        if (fileData.value) {
            currentStep.value = 2;
        }
    } else if (currentStep.value === 2) {
        // 매핑 완료 확인 후 Validation 실행 (2단계 유지)
        if (contentSectionRef.value) {
            contentSectionRef.value.handleNextClick();
            // Validation 완료 후에도 2단계에 유지 (3단계로 이동하지 않음)
        }
    } else if (currentStep.value === 3) {
        // 3단계는 사용하지 않음 (2단계에서 Validation 완료 후 Upload)
    }
};

// Upload 버튼 클릭
const handleUpload = async () => {
    if (!canUpload.value || !fileData.value || !contactType.value) return;
    
    // Validation 체크
    const hasDuplicates = validatedData.value.some(row => row.isDuplicate);
    if (hasDuplicates) {
        await alert({
            title: t('contact.Error') || 'Error',
            message: t('contact.Please remove duplicate data before uploading') || 'Please remove duplicate data before uploading.',
            type: 'error'
        });
        return;
    }
    
    const hasMissingRequired = validatedData.value.some(row => row.isMissingRequired);
    if (hasMissingRequired) {
        await alert({
            title: t('contact.Error') || 'Error',
            message: t('contact.Please fill in all required fields before uploading') || 'Please fill in all required fields before uploading.',
            type: 'error'
        });
        return;
    }
    
    try {
        isUploading.value = true;
        
        console.log('업로드 시작:', {
            contactType: contactType.value,
            validatedDataCount: validatedData.value.length,
            validatedData: validatedData.value
        });
        
        // ValidatedRow를 CreateIndividualData 또는 CreateCorporateData로 변환
        const uploadData = validatedData.value
            .filter(row => !row.isDuplicate && !row.isMissingRequired)
            .map(row => {
                if (contactType.value === 'Individual') {
                    const data: CreateIndividualData = {
                        identificationType: row.data['ID_TYPE_CD'] || '',
                        identificationNo: row.data['CONTACT_REG_NO'] || '',
                        nationality: row.data['NATIONALITY_CD'] || '',
                        dateOfBirth: row.data['DATE_OF_BIRTH'] || undefined,
                        firstName: row.data['FIRST_NAME'] || '',
                        // 공백 문자열도 유효한 값으로 처리 (|| undefined 대신 명시적 체크)
                        middleName: row.data['MIDDLE_NAME'] !== undefined && row.data['MIDDLE_NAME'] !== null 
                            ? String(row.data['MIDDLE_NAME']) 
                            : undefined,
                        surname: row.data['SURNAME'] || '',
                        addressType: 'B07001', // 기본값
                        contactInfo: {
                            countryCode: row.data['COUNTRY_CD'] || '',
                            // 공백 문자열도 유효한 값으로 처리
                            areaCode: row.data['AREA_CODE'] !== undefined && row.data['AREA_CODE'] !== null 
                                ? String(row.data['AREA_CODE']) 
                                : undefined,
                            mobileNos: row.data['MOBILE_NO'] ? [row.data['MOBILE_NO']] : [],
                            emails: [
                                row.data['CONTACT_EMAIL'] || undefined,
                                row.data['CONTACT_EMAIL_2'] || undefined
                            ].filter(email => email && email.trim()),
                            note: row.data['NOTE'] !== undefined && row.data['NOTE'] !== null 
                                ? String(row.data['NOTE']) 
                                : undefined,
                        }
                    };
                    return data;
                } else {
                    const data: CreateCorporateData = {
                        jurisdiction: row.data['JURISDICTION'] || '',
                        entityName: row.data['CONTACT_NAME'] || '',
                        incorporationDate: row.data['DATE_OF_INCORPORATION'] || undefined,
                        publicAccountingEntities: (row.data['AUDITOR_YN'] === 'Yes' || row.data['AUDITOR_YN'] === true) ? 'Yes' : 'No',
                        registrationNumber: row.data['CONTACT_REG_NO'] || '',
                        // Representative Name은 File Upload에서 제외
                        addressType: 'B07001', // 기본값
                        contactInfo: {
                            countryCode: row.data['COUNTRY_CD'] || '',
                            // 공백 문자열도 유효한 값으로 처리
                            areaCode: row.data['AREA_CODE'] !== undefined && row.data['AREA_CODE'] !== null 
                                ? String(row.data['AREA_CODE']) 
                                : undefined,
                            mobileNos: row.data['MOBILE_NO'] ? [row.data['MOBILE_NO']] : [],
                            emails: [
                                row.data['CONTACT_EMAIL'] || undefined,
                                row.data['CONTACT_EMAIL_2'] || undefined
                            ].filter(email => email && email.trim()),
                            note: row.data['NOTE'] !== undefined && row.data['NOTE'] !== null 
                                ? String(row.data['NOTE']) 
                                : undefined,
                        }
                    };
                    return data;
                }
            });
        
        console.log('업로드 데이터 생성 완료:', {
            uploadDataCount: uploadData.length,
            uploadData: uploadData
        });
        
        if (uploadData.length === 0) {
            await alert({
                title: t('contact.Error') || 'Error',
                message: t('contact.No data to upload. Please check validation again') || 'No data to upload. Please check validation again.',
                type: 'error'
            });
            isUploading.value = false;
            return;
        }
        
        // 백엔드 API 호출
        console.log('API 호출 시작:', {
            contactType: contactType.value,
            dataCount: uploadData.length
        });
        
        const response = await contactsApi.uploadContacts({
            contactType: contactType.value as 'Individual' | 'Corporate',
            data: uploadData
        });
        
        console.log('API 응답:', response);
        console.log('API 응답 구조:', {
            response: response,
            hasSuccess: !!(response as any).success,
            hasTotal: !!(response as any).total,
            hasData: !!(response as any).data,
            keys: Object.keys(response || {}),
            responseType: typeof response
        });
        
        // sendSuccessResponse는 { success: true, ...data } 형태로 응답
        // apiClient.post는 response.data를 반환하므로, response는 { success: true, total, success (count), failed, results } 형태
        // 주의: JavaScript 객체에서는 같은 키가 두 개일 수 없으므로, success는 마지막 값(number)만 유지됨
        const apiResponse = response as any;
        
        // 응답 성공 여부 확인 (total이 있으면 성공 응답)
        if (apiResponse && apiResponse.total !== undefined) {
            // 성공 응답인 경우
            const total = apiResponse.total || 0;
            const failed = apiResponse.failed || 0;
            const successCount = apiResponse.success || (total - failed); // success는 number (성공 개수)
            const results = apiResponse.results || [];
            
            const uploadResult = {
                total,
                success: successCount,
                failed,
                results
            };
            
            console.log('업로드 결과:', uploadResult);
            
            // 성공 메시지 표시 (ContactCreateIndividualPage와 동일한 패턴)
            // 백엔드 응답에는 message 필드가 없으므로, 기본 메시지 사용
            if (uploadResult.success > 0) {
                // 성공 메시지 (배치 업로드용 - 개수 포함)
                const successMessageTemplate = t('contact.Contacts uploaded successfully') || '{{count}} contact(s) uploaded successfully';
                const successMessage = successMessageTemplate.replace('{{count}}', String(uploadResult.success));
                
                await alert({
                    title: t('contact.Success') || 'Success',
                    message: successMessage,
                    type: 'success'
                });
            }
            
            // 실패한 항목이 있으면 경고 메시지 표시 (성공 메시지 후에 표시)
            if (uploadResult.failed > 0) {
                const failedItems = uploadResult.results.filter((r: any) => !r.success);
                console.error('실패한 항목:', failedItems);
                if (failedItems.length > 0) {
                    const rowLabel = t('contact.Row') || 'Row';
                    const unknownError = t('contact.Unknown error') || 'Unknown error';
                    const errorMessages = failedItems.map((item: any) => 
                        `${rowLabel} ${item.index + 1}: ${item.error || unknownError}`
                    ).join('\n');
                    await alert({
                        title: t('contact.Warning') || 'Warning',
                        message: errorMessages,
                        type: 'warning'
                    });
                }
            }
            
            // Contact 목록 새로고침 이벤트 발생 (ContactCreateIndividualPage와 동일한 패턴)
            // 다이얼로그를 닫기 전에 이벤트 발생하여 데이터 새로고침 보장
            window.dispatchEvent(new CustomEvent('contact-list-refresh'));
            emit('upload-complete');
            handleCancel();
        } else if (apiResponse && apiResponse.success === false) {
            // 실패 응답인 경우
            console.error('API 응답이 실패했습니다:', apiResponse);
            const errorMessage = apiResponse.error?.message || apiResponse.message || t('contact.Failed to upload contacts') || 'Failed to upload contacts';
            await alert({
                title: t('contact.Error') || 'Error',
                message: errorMessage,
                type: 'error'
            });
        } else {
            console.error('응답 데이터가 없거나 형식이 올바르지 않습니다:', {
                response: response,
                apiResponse: apiResponse,
                responseKeys: response ? Object.keys(response) : [],
                responseString: JSON.stringify(response, null, 2)
            });
            await alert({
                title: t('contact.Error') || 'Error',
                message: t('contact.Invalid response format. Please check the console for details') || 'Invalid response format. Please check the console for details.',
                type: 'error'
            });
        }
    } catch (error: any) {
        console.error('Upload failed:', error);
        console.error('Error details:', {
            message: error.message,
            response: error.response,
            responseData: error.response?.data
        });
        
        // 에러 메시지 추출 (ContactCreateIndividualPage와 동일한 패턴)
        const responseData = error?.response?.data;
        let errorMessage = '';
        if (responseData?.error) {
            if (typeof responseData.error === 'object' && responseData.error.message) {
                errorMessage = responseData.error.message;
            } else if (typeof responseData.error === 'string') {
                errorMessage = responseData.error;
            }
        }
        
        // 백엔드에서 반환한 다국어 메시지만 사용
        if (!errorMessage) {
            errorMessage = responseData?.message || error?.message || t('contact.Failed to upload contacts') || 'Failed to upload contacts';
        }
        
        await alert({
            title: t('contact.Error') || 'Error',
            message: errorMessage,
            type: 'error'
        });
    } finally {
        isUploading.value = false;
    }
};

// Cancel 버튼 클릭
const handleCancel = () => {
    // 상태 초기화
    currentStep.value = 1;
    contactType.value = 'Individual';
    uploadedFile.value = null;
    fileData.value = null;
    mappings.value = {};
    validatedData.value = [];
    dialog.value = false;
};
</script>

<template>
    <v-dialog v-model="dialog" max-width="1836px" width="97%">
        <v-card>
            <v-card-title class="d-flex justify-space-between align-center">
                <span>Contact File Upload</span>
                <v-btn icon variant="text" @click="handleCancel">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            
            <v-card-text>
                <!-- 1단계: 파일 업로드 섹션 -->
                <ContactFileUploadSection
                    v-if="currentStep === 1"
                    v-model:contactType="contactType"
                    v-model:file="uploadedFile"
                    @file-uploaded="handleFileUploaded"
                />
                
                <!-- 2-4단계: 데이터 비교 및 매핑 섹션 -->
                <ContactFileUploadContentSection
                    v-if="currentStep >= 2 && fileData"
                    ref="contentSectionRef"
                    :contactType="contactType as 'Individual' | 'Corporate'"
                    :fileData="fileData"
                    :mappings="mappings"
                    :validatedData="validatedData"
                    :currentStep="currentStep as 2 | 3 | 4"
                    @mapping-updated="handleMappingUpdated"
                    @validation-complete="handleValidationComplete"
                    @step-changed="(step) => currentStep = step as 1 | 2 | 3 | 4"
                />
            </v-card-text>
            
            <v-card-actions>
                <v-btn @click="handleCancel">Cancel</v-btn>
                <v-spacer></v-spacer>
                <v-btn
                    v-permission-button
                    data-menu-key="/Contact"
                    data-button-name="Next"
                    v-if="currentStep === 2 && !canUpload"
                    color="primary"
                    @click="handleNext"
                    :disabled="!canProceedToNext"
                >
                    Next
                </v-btn>
                <v-btn
                    v-permission-button
                    data-menu-key="/Contact"
                    data-button-name="Upload"
                    v-if="currentStep === 2 && canUpload"
                    color="primary"
                    @click="handleUpload"
                    :disabled="!canUpload"
                    :loading="isUploading"
                >
                    Upload
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped lang="scss">
// 추가 스타일이 필요한 경우 여기에 작성
</style>


